import { useCallback } from 'react';
import type { QueryOptions } from '@apollo/client';
import type {
  CreateMembershipsInput,
  CreateMembershipsMutationResult,
  Resource,
} from '@frameio/graphql-client/generated';
import { useCreateMembershipsMutation } from '@frameio/graphql-client/generated';
import { intl, plural } from '@frameio/i18n';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { toSupportedLocaleOrDefault } from '@frameio/shared/utils/toSupportedLocale';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import {
  updateAccountUsersCache,
  updateDirectMembershipsCache,
  updateSuggestedAccountUsersCache,
} from '../cache-ops/memberships';
import TEXT from './generated/TEXT/useCreateMemberships';
import { useResourceName } from './useResourceName';

TEXT.DEFINE({
  bulk: {
    /** a part of the success toast message, which references groups if any */
    groups: (groupCount: number) =>
      plural(groupCount, {
        zero: '',
        one: '1 Group ',
        other: `${groupCount} Groups `,
      }),
    /** a part of the success toast message, which references members if any */
    members: (memberCount: number) =>
      plural(memberCount, {
        zero: '',
        one: '1 Member ',
        other: `${memberCount} Members `,
      }),
    /** a part of the success toast message, which references guests if any */
    guests: (guestCount: number) =>
      plural(guestCount, {
        zero: '',
        one: '1 Guest ',
        other: `${guestCount} Guests `,
      }),
    /** a part of the success toast message, which will be inserted between above parts if there is more than one part */
    and: 'and',
    /** a part of the success toast message that will come after the above parts are listed and will mention the name of the resource to which they are added */
    addedTo: (resourceName: string) => ` added to ${resourceName}`,

    /** Error toast message when bulk member addition fails */
    error: (count: number, resourceName: string) =>
      `Unable to add ${count} members to ${resourceName}`,
  },

  single: {
    /** Success toast message when adding a single member to resource */
    success: (userType: 'Guest' | 'Member' | 'Group', resourceName: string) =>
      `${userType} added to ${resourceName}`,

    /** Error toast message when single member addition fails */
    error: (resourceName: string) =>
      `Unable to add membership to ${resourceName}`,
  },

  singleNew: {
    /** Success toast message when sending invitation to new user */
    success: (memberEmail: string) => `Invitation sent to ${memberEmail}`,

    /** Error toast message when invitation fails */
    error: (resourceName: string) => `Unable to invite to ${resourceName}`,
  },

  /** Error message when resource ID is missing */
  missingResourceIdError: 'Unable to add membership',
});

type CreateMembershipsCallbackArgs = {
  members: CreateMembershipsInput['members'];
  message?: string;
} & Pick<
  CreateMembershipsInput,
  'roleId' | 'licenseType' | 'message' | 'notify'
>;

export const useCreateMemberships = ({
  resourceId,
  resourceType,
  refetchQueries,
}: {
  resourceId?: Resource['id'];
  resourceType: string;
  refetchQueries?: Array<QueryOptions>;
}) => {
  const accountId = useActiveAccountId();
  const locale = toSupportedLocaleOrDefault(intl().locale);
  const resourceName = useResourceName(resourceId, resourceType);
  const tryCatchOnSubmit = useToastTryCatchHandler();
  const [createMembershipsMutation] = useCreateMembershipsMutation();

  const createMemberships = useCallback(
    async ({
      licenseType,
      roleId,
      members,
      message,
      notify,
    }: CreateMembershipsCallbackArgs) =>
      tryCatchOnSubmit<CreateMembershipsMutationResult, any>({
        attempt: () => {
          if (!resourceId) {
            return Promise.reject(TEXT.missingResourceIdError);
          }
          return createMembershipsMutation({
            variables: {
              locale,
              input: {
                members,
                roleId,
                licenseType,
                notify,
                message,
                resource: {
                  projectId:
                    resourceType === 'Project' ? resourceId : undefined,
                  workspaceId:
                    resourceType === 'Workspace' ? resourceId : undefined,
                  assetId:
                    resourceType === 'FolderAsset' ? resourceId : undefined,
                },
              },
            },
            refetchQueries: ['GetAccountUsage', ...(refetchQueries || [])],
            update: (cache, { data }) => {
              const { memberships } = data?.createMemberships ?? {};
              if (!memberships) return;

              updateAccountUsersCache(cache, memberships, accountId);
              updateDirectMembershipsCache(
                cache,
                memberships,
                resourceId,
                locale,
                accountId
              );
              updateSuggestedAccountUsersCache(cache, memberships, resourceId);
            },
          });
        },
        successMessage: ({ data }) => {
          if (!data?.createMemberships?.memberships) {
            return '';
          }

          const bulkMemberships = data?.createMemberships.memberships;

          if (bulkMemberships.length > 1) {
            const groupCount = bulkMemberships.filter((membership) =>
              Boolean(membership?.accountUserGroup)
            ).length;

            const guestCount = bulkMemberships.filter((membership) =>
              Boolean(membership?.accountUser?.licenseType === 'GUEST')
            ).length;

            const userCount =
              bulkMemberships.length - (groupCount + guestCount);

            return constructedSuccessMessage(
              groupCount,
              userCount,
              licenseType === 'GUEST' ? guestCount : 0,
              resourceName ?? ''
            );
          }

          const { accountUser, accountUserGroup } = bulkMemberships[0];

          if (accountUser?.isPending) {
            return TEXT.singleNew.success(
              accountUser.inviteEmailAddress
            ) as string;
          }

          if (accountUserGroup) {
            return TEXT.single.success('Group', resourceName) as string;
          }

          return TEXT.single.success(
            accountUser.licenseType === 'GUEST' ? 'Guest' : 'Member',
            resourceName
          ) as string;
        },
        errorMessage() {
          if (members.length > 1) {
            return TEXT.bulk.error(members.length, resourceName) as string;
          }

          const member = members[0];
          if (member.email) {
            return TEXT.singleNew.error(resourceName) as string;
          }

          return TEXT.single.error(resourceName) as string;
        },
      }),
    [
      accountId,
      createMembershipsMutation,
      locale,
      refetchQueries,
      resourceId,
      resourceName,
      resourceType,
      tryCatchOnSubmit,
    ]
  );

  return createMemberships;
};

/** This fixes the crash that was caused by using plural() outside of TEXT.DEFINE().
 * The solution is not ideal because the ultimate string construction for the success
 * message is happening outside of TEXT, but it's a fairly simple sentence and will
 * hopefully work in most languages. If not, this could be refactored further, but
 * we'll probably need to get product involved and possibly replace the entire
 * success message with a less complex one.
 */
const constructedSuccessMessage = (
  groupCount: number,
  memberCount: number,
  guestCount: number,
  resourceNameString: string
) => {
  let combinedMessage = '';
  const parts = [
    TEXT.bulk.groups(groupCount),
    TEXT.bulk.members(memberCount),
    TEXT.bulk.guests(guestCount),
  ].filter((item) => item !== '');

  if (parts.length === 3) {
    combinedMessage = `${parts[0]}, ${parts[1]}, ${TEXT.bulk.and} ${parts[2]}`;
  } else if (parts.length === 2) {
    combinedMessage = `${parts[0]} ${TEXT.bulk.and} ${parts[1]}`;
  } else if (parts.length === 1) {
    combinedMessage = parts[0] as string;
  }

  if (combinedMessage.length) {
    combinedMessage += `${TEXT.bulk.addedTo(resourceNameString)}`;
  }

  return combinedMessage;
};
